import { useCallback, useEffect, useRef } from 'react';
import { notifyError } from '../../../../utils';

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000;
const POLL_START_HOUR = 6;
const POLL_END_HOUR = 20;

const isWithinPollingWindow = (date = new Date()) => {
  const minutes = date.getHours() * 60 + date.getMinutes();
  return (
    minutes >= POLL_START_HOUR * 60 && minutes < POLL_END_HOUR * 60
  );
};

const getMillisecondsUntilNextWindow = (date = new Date()) => {
  const nextDate = new Date(date);

  if (date.getHours() < POLL_START_HOUR) {
    nextDate.setHours(POLL_START_HOUR, 0, 0, 0);
    return nextDate.getTime() - date.getTime();
  }

  nextDate.setDate(nextDate.getDate() + 1);
  nextDate.setHours(POLL_START_HOUR, 0, 0, 0);
  return nextDate.getTime() - date.getTime();
};

/**
 * Hook que executa `refresh` periodicamente:
 * - A cada `intervalMs` ms (default 5min) durante a janela de polling.
 * - Janela = 06h-20h por padrão; passe `window24h: true` pra exibicao
 *   em TV de CCO (opera 24/7).
 * - Pausado quando a aba não está visível (volta ao ficar visível).
 * - Limpa timer ao desmontar.
 *
 * Quando opções mudam em runtime (ex: usuário entra em fullscreen e
 * trocamos pra 60s/24h), o effect re-roda e o novo schedule assume.
 *
 * Uso:
 *   useDashboardPolling(refreshFn);
 *   useDashboardPolling(refreshFn, { intervalMs: 60_000, window24h: true });
 */
export const useDashboardPolling = (refresh, options = {}) => {
  const { intervalMs = DEFAULT_POLL_INTERVAL_MS, window24h = false } = options;
  const timeoutRef = useRef(null);
  const refreshRef = useRef(refresh);
  const mountedRef = useRef(true);

  // mantém refresh atualizada sem recriar effects
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    clearTimer();
    if (document.visibilityState !== 'visible') return;

    // window24h pula a verificacao de horario — polling roda a noite
    // toda. Usado no modo TV/fullscreen.
    const delay = window24h || isWithinPollingWindow()
      ? intervalMs
      : getMillisecondsUntilNextWindow();

    timeoutRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      try {
        await refreshRef.current();
      } catch {
        if (mountedRef.current) {
          notifyError(
            'Não foi possível trazer as informações das UCHAS e EBAS'
          );
        }
      } finally {
        if (mountedRef.current) schedule();
      }
    }, delay);
  }, [clearTimer, intervalMs, window24h]);

  useEffect(() => {
    mountedRef.current = true;

    const runOnce = async () => {
      try {
        await refreshRef.current();
      } catch {
        if (mountedRef.current) {
          notifyError(
            'Não foi possível trazer as informações das UCHAS e EBAS'
          );
        }
      } finally {
        if (mountedRef.current) schedule();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        clearTimer();
        return;
      }
      runOnce();
    };

    runOnce();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [schedule, clearTimer]);
};
